/**
 * Form to input parameters and constraints based on which test cases are generated in the ctd tool
 * @param {boolean} isLab
 * @param {object} parameters
 * @param {function} setParameters
*/
import Styles from './ParamsAndConstrainsts.module.scss'
import { useRef } from 'react';

// component imports
import { useTranslation } from 'components/lib';
import { ConstraintsForm } from './ConstraintsForm/ConstraintsForm';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import FeatherIcon from 'feather-icons-react';

export function ParamsAndConstraints({ isLab, parameters, setParameters }) {
    const fileInputRef = useRef(null);
    const { t } = useTranslation();

    const handleFileInputChange = (e) => {
        // function to upload csv data containting the parameters
        const selectedFile = e.target.files[0];

        if (!selectedFile) {
            alert(t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.alert.selectFile'))
            return;
        }

        const fileType = selectedFile.name.split('.').pop()
        if (fileType.toLowerCase() !== 'csv') {
            alert(t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.alert.fileCSV'))
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const csvData = e.target.result;

            const rows = csvData.trim().split('\n')
            const records = rows.map(row => row.split(',').map(item => item.trim()));
            const importedParams = records.map(row => {
                return {
                    key: row[0],
                    values: row.slice(1).filter(r => r !== "")
                }
            })
            setParameters(prevState => ({ ...prevState, parameters: importedParams }))
        };
        reader.readAsText(selectedFile);
        e.target.value = null;

    };

    function addParameters() {
        //function adds a new set of parameters, with 2 empty values by default
        const newParam = {
            key: "",
            values: ["", ""]
        }
        setParameters(prevState => ({
            ...prevState,
            parameters: [...parameters.parameters, newParam]
        }))
    }

    function removeParameter(removeIndex) {
        //function deletes an entire set of parameters
        const removedParameter = parameters.parameters.filter((_, index) => index === removeIndex);
        const updatedParams = parameters.parameters.filter((_, index) => index !== removeIndex);

        const updatedReqTest = parameters.requiredTests.map((test) => {
            const dataToReturn = test.assigns.filter(assign => assign.param !== removedParameter[0].key)
            return { name: "", assigns: dataToReturn }
        })


        setParameters(prevState => ({
            ...prevState,
            parameters: updatedParams,
            requiredTests: updatedReqTest
        }));


    }

    function addValues(currentObject) {
        //function adds new empty values to a parameter set
        setParameters(prevParams => {
            return {
                ...prevParams,
                parameters: prevParams.parameters.map((param, index) => {
                    if (index === currentObject) {
                        return {
                            ...param,
                            values: [...param.values, '']
                        };
                    }
                    return param;
                })
            };
        });
    }

    function deleteValues(currentObject, valueIndex, value, key) {
        //function deletes individual values from a  parameter set
        setParameters(prevParams => {
            return {
                ...prevParams,
                parameters: prevParams?.parameters?.map((param, index) => {
                    if (index === currentObject) {
                        return {
                            ...param,
                            values: param.values.filter((_, i) => i !== valueIndex)
                        }
                    }
                    return param
                }),
                requiredTests: prevParams?.requiredTests?.map(test => {
                    test.assigns.map((assign) => {
                        if (assign.param === key) {
                            assign.value = ""
                        }
                        return assign;
                    })
                    return test;
                })
            }
        })
    }

    function editKeys(objIndex, value) {
        //function to edit title of a parameter set
        setParameters(prevParams => {
            return {
                ...prevParams,
                parameters: prevParams?.parameters?.map((param, index) => {
                    if (index === objIndex) {
                        return {
                            ...param,
                            key: value
                        }
                    }
                    return param;
                })
            }
        })
    }

    function editValues(objIndex, valueIndex, value) {
        //function to edit individual values in a  parameter set
        setParameters(prevParams => {
            const updateValues = [...prevParams.parameters];
            updateValues[objIndex].values[valueIndex] = value;
            return { ...prevParams, parameters: updateValues }
        })
    }

    function addConstraints() {
        //function to add a new empty constraint
        const newConstr = {
            "secs": [
                {
                    "param": "",
                    "bind": "",
                    "value": "",
                    "between": ""
                },
                {
                    "param": "",
                    "bind": "",
                    "value": ""
                }
            ],
            "type": ""
        }
        setParameters(prevState => ({
            ...prevState,
            constraints: [...prevState.constraints, newConstr]
        }))
    }

    function editConstraints(field, value, objIndex, secIndex) {
        //function to edit/populate the fields in a constraint
        setParameters((prevState) => ({
            ...prevState,
            constraints: prevState?.constraints?.map((constr, index) => {
                if (index === objIndex) {
                    if (field === 'type') {

                        return {
                            ...constr,
                            type: value,
                        };
                    } else {
                        return {
                            ...constr,
                            secs: constr.secs.map((sec, idx) => {
                                if (idx === secIndex) {
                                    return {
                                        ...sec,
                                        [field]: value,
                                    };
                                }
                                return sec;
                            }),
                        };
                    }
                }
                return constr;
            }),
        }));


    }

    function deleteConstraints(objIndex) {
        //function to delete a constraint
        setParameters(prevState => ({
            ...prevState,
            constraints: prevState.constraints.filter((_, i) => i !== objIndex)
        }))
    }

    function addConstraintClause(objIndex, secIndex) {
        //function that lets you add more conditions to the same constraint
        const newClause = {
            "param": "",
            "bind": "",
            "value": "",
        }
        setParameters((prevState) => {
            const constraintsCopy = [...prevState.constraints];
            const secsCopy = [...constraintsCopy[objIndex].secs];
            secsCopy.splice(secIndex + 1, 0, newClause);
            if (secIndex >= 0 && !secsCopy[secIndex].hasOwnProperty("between")) {
                secsCopy[secIndex].between = "";
            }
            if (secIndex + 1 !== secsCopy.length - 1 && !secsCopy[secIndex + 1].hasOwnProperty("between")) {
                secsCopy[secIndex + 1].between = "";
            }
            constraintsCopy[objIndex].secs = secsCopy;
            return {
                ...prevState,
                constraints: constraintsCopy,
            };
        })

    }


    function deleteConstraintClause(objIndex, secIndex) {
        //function that allows to delete the parts of a constraint
        setParameters(prevState => ({
            ...prevState,
            constraints: prevState.constraints.map((constraint, i) => {
                if (i === objIndex) {
                    const newSecs = constraint.secs.filter((_, j) => j !== secIndex);
                    if (secIndex === constraint.secs.length - 1) {
                        const prevIndex = secIndex - 1;
                        if (prevIndex >= 0) {
                            delete newSecs[prevIndex].between;
                        }
                    }
                    return {
                        ...constraint,
                        secs: newSecs
                    };

                }
                return constraint;
            })
        }))
    }

    return (
        <div className={Styles.parentContainer_params}>

            <div className={Styles.header}>
                <p>{t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.header')}</p>
                <span>
                    <AivagamButton
                        onClick={addParameters}
                        buttonText={
                            <div style={{ display: 'flex', gap: '0.5em', height: '1em', alignItems: 'center' }}>
                                <FeatherIcon size='1.5em' icon='plus-circle' />
                                {t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.button.parameter')}
                            </div>
                        }
                        classStyles={{ height: 'fit-content' }}
                        type='action'
                    />
                    <AivagamButton
                        onClick={addConstraints}
                        buttonText={
                            <div style={{ display: 'flex', gap: '0.5em', height: '1em', alignItems: 'center' }}>
                                <FeatherIcon size='1.5em' icon='plus-circle' />
                                {t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.button.constraint')} </div>
                        }
                        classStyles={{ height: 'fit-content' }}
                        type='action'
                    />
                    <input
                        type="file"
                        id="fileInput"
                        ref={fileInputRef}
                        title={t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.input.importCSV')}
                        style={{ display: "none" }}
                        onChange={handleFileInputChange}
                    />

                </span>
            </div>
            <div className={Styles.params_body}>
                <span style={{ display: 'flex', gap: '1em' }}>
                    <p>{t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.title.parameters')}</p>
                    <AivagamButton
                        onClick={() => { fileInputRef.current.click(); }}
                        // buttonText='Import from CSV'
                        classStyles={{ display: isLab && 'none' }}
                        buttonText={
                            <div style={{ display: 'flex', gap: '0.5em', height: '1em', alignItems: 'center' }}>
                                {t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.button.importCSV')}
                            </div>
                        }

                    />
                </span>
                {
                    parameters?.parameters?.map((param, index) => (
                        <div key={`${index}_Key`} style={{ display: 'flex', gap: '0.5em', alignItems: 'flex-start' }}>
                            <p style={{ fontWeight: 'normal', fontSize: '1em', width: '2%', textAlign: 'center' }}>{index + 1}.</p>
                            <span style={{ minWidth: '13%', marginRight: isLab && '0.2em' }}>
                                <div className={`${Styles.parmaInput} ${Styles.paramTitleCont}`}>
                                    <input value={param.key} onChange={(e) => { editKeys(index, e.target.value) }} placeholder={t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.placeholder.parameter', { index: index + 1 })} style={{ padding: '0.2em', fontSize: '0.8em', height: "100%" }} />
                                    <button title={t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.button.deleteParam')}><FeatherIcon size='1.1em' icon='x' role='button' onClick={() => { removeParameter(index) }} /></button>
                                </div>
                            </span>
                            <div className={Styles.valuesContainer}>
                                {
                                    param.values.map((val, valIndex) => (
                                        <div key={`val_${index}_${valIndex}`} style={{ flexBasis: 'calc(25% - 0.8em) !important' }} className={`${Styles.parmaInput} `}>
                                            <input value={val} onChange={(e) => { editValues(index, valIndex, e.target.value) }} placeholder={t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.placeholder.value', { index: valIndex + 1 })} style={{ padding: '0.2em', fontSize: '0.8em', height: "100%" }} />
                                            <button title={t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.button.deleteVal')}><FeatherIcon size='1.1em' icon='x' role='button' onClick={() => { deleteValues(index, valIndex, val, param.key) }} /></button>
                                        </div>
                                    ))
                                }
                                <button title={t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.button.addValue')}><FeatherIcon size='1.1em' icon='plus-circle' color='green' role='button' onClick={() => { addValues(index) }} /></button>
                            </div>
                        </div>
                    ))
                }
                <AivagamButton
                    onClick={addParameters}
                    buttonText={
                        <div style={{ display: 'flex', gap: '0.5em' }}>
                            <FeatherIcon size='1.5em' icon='plus-circle' />
                            {t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.button.addParam')} </div>
                    }
                    classStyles={{ height: 'fit-content' }}
                    type='action'
                />
                <p>{t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.title.constraints')}</p>
                {
                    parameters?.constraints?.map((constr, index) => (
                        <div key={`constraint_${index}`} style={{ display: 'flex', gap: '0.5em', alignItems: 'flex-start', marginBottom: '0.8em' }}>
                            <p style={{ fontWeight: 'normal', fontSize: '1em', width: '2%', textAlign: 'center' }}>{index + 1}.</p>
                            <ConstraintsForm
                                parameters={parameters.parameters}
                                constraints={constr}
                                selectFunction={editConstraints}
                                index={index}
                                deleteConstraints={deleteConstraints}
                                addNewClause={addConstraintClause}
                                removeClause={deleteConstraintClause}
                            />
                        </div>

                    ))
                }
                <AivagamButton
                    onClick={addConstraints}
                    buttonText={
                        <div style={{ display: 'flex', gap: '0.5em' }}>
                            <FeatherIcon size='1.5em' icon='plus-circle' />
                            {t('Components.NodeLayout.CombinatorialTestPane.paramsAndConstraints.button.addConstraint')}
                        </div>
                    }
                    classStyles={{ height: 'fit-content' }}
                    type='action'
                />
            </div>
        </div>
    )
}
